import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
  ChangeDetectionStrategy
} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {CheckResultControllerService, IFCCheck, Task} from '../../../../../../api';
import {TaskDetailsService} from "../../../../../../services/tasks/task-details.service";
import {faCircleCheck, faCircleXmark} from "@fortawesome/pro-solid-svg-icons";
import {DatePipe, DecimalPipe} from '@angular/common';
import {HttpClient} from '@angular/common/http';
import {LodPipe} from '../../../../../../pipes/lod.pipe';
import {LoiCheckStepperComponent} from '../../loi-check-stepper/loi-check-stepper.component';
import {OverviewReportComponent} from '../../../../overviewReport/overview-report.component';
import {
  BicBadgeComponent,
  BicResultColumnComponent,
  BicResultComponent,
  BicResultHeaderComponent,
  BicResultRowComponent
} from "@buildinginformationcloud/bic-ui-components";
import {FaIconComponent} from "@fortawesome/angular-fontawesome";
import {faSpinnerThird} from "@fortawesome/pro-duotone-svg-icons";
import {IfcCheckPrefetchService} from '../../../../../../services/prefetch/ifc-check-prefetch.service';
import {BimViewerService, TopicCreationData} from "../../../../../../services/bim-viewer/bim-viewer.service";
import {StateConfigService} from "../../../../../../services/state/state-config.service";
import {
  DEFAULT_OVERALL_THRESHOLDS,
  statusFromIdsThresholds,
} from '../../../../../../models/report/ids-thresholds';
import {createMemo} from '../../../../../../memo';

export interface IdsFacet {
  facetType: string;
  facetTypeLabel: string;
  value: string | null;
  values: string[];
}

export interface IdsRequirement extends IdsFacet {
  name: string;
  cardinality: string;
  result: number;
  status: boolean;
  guidListCorrect: string[];
  guidListFail: string[];
}

export interface IdsSpecification {
  name: string;
  status: boolean;
  applicabilities: IdsFacet[];
  totalElements: number;
  result: number;
  requirements: IdsRequirement[];
}

export interface IdsCheckResult {
  score: number;
  specifications: IdsSpecification[];
}

@Component({
  selector: 'app-ids-check-report',
  templateUrl: './ids-check-report.component.html',
  styleUrls: ['./ids-check-report.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    LoiCheckStepperComponent,
    OverviewReportComponent,
    BicBadgeComponent,
    BicResultColumnComponent,
    BicResultComponent,
    BicResultHeaderComponent,
    BicResultRowComponent,
    FaIconComponent,
    DecimalPipe
  ]
})
export class IdsCheckReportComponent implements OnInit, AfterViewInit, OnDestroy {

  /** Stabile Rueckgabewerte fuer Template-Bindungen - siehe memo.ts. */
  private readonly memo = createMemo();


  idsCheckResult: IdsCheckResult = {
    score: 0,
    specifications: []
  };

  /**
   * Das Gesamtergebnis als ein Kreis mit Ampelfarbe (#2317).
   *
   * <p>Dieser Bericht zeigte schon immer nur einen Kreis — allerdings über `checkType="LOI"` und
   * damit konstant grün, unabhängig vom Wert. Die Stufen sind dieselben wie in der
   * myCheck-Berichtsansicht; dieser Pfad hat keinen Zugriff auf die Konfiguration des
   * Prüfregelsets, deshalb der Default.
   */
  get overallResult(): { percent: number | null; status: 'success' | 'warning' | 'error' | 'unknown' } {
    const percent = (this.idsCheckResult.score ?? 0) * 100;
    // Gecacht, weil das Objekt an [singleResult] haengt (siehe memo.ts). NaN taugt
    // nicht als Abhaengigkeit (NaN !== NaN), deshalb ein fester Platzhalter.
    const finite = Number.isFinite(percent);
    return this.memo('overallResult', [finite ? percent : 'invalid'],
      (): { percent: number | null; status: 'success' | 'warning' | 'error' | 'unknown' } => finite
        ? {percent, status: statusFromIdsThresholds(percent, DEFAULT_OVERALL_THRESHOLDS)}
        : {percent: null, status: 'unknown'});
  }

  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private lodPipe = inject(LodPipe);
  private datePipe = inject(DatePipe);
  private ifcCheckPrefetchService = inject(IfcCheckPrefetchService);
  private checkResultService = inject(CheckResultControllerService);
  private taskDetailService = inject(TaskDetailsService);
  private http = inject(HttpClient);
  private bimViewerService = inject(BimViewerService);
  private stateConfigService = inject(StateConfigService);

  check: IFCCheck = {};
  loading = true;
  selectedSpecification: IdsSpecification | undefined;

  protected readonly faCircleCheck = faCircleCheck;
  protected readonly faCircleXmark = faCircleXmark;
  protected readonly faSpinnerThird = faSpinnerThird;

  @ViewChildren('applicabilityContainer') applicabilityContainers!: QueryList<ElementRef<HTMLDivElement>>;
  @ViewChildren('valueContainer') valueContainers!: QueryList<ElementRef<HTMLDivElement>>;

  private zone = inject(NgZone);
  private resizeObserver: ResizeObserver | null = null;
  private observedElements = new Set<Element>();


  get checkerName(): string {
    return this.check.checker?.firstName
      ? this.check.checker.firstName + ' ' + this.check.checker.lastName
      : 'Unbekannt';
  }

  get fileSize(): string {
    if (this.check.projectFile?.size) {
      const sizeInMB = this.check.projectFile.size / (1024 * 1024);
      return sizeInMB.toFixed(2) + ' MB';
    }
    return 'Unbekannt';
  }

  get fileName(): string {
    return this.check.projectFile?.name ?? 'Unbekannt';
  }

  get ruleName(): string {
    if (this.check.loiCheckRule?.name) {
      return this.check.loiCheckRule.name;
    } else if (this.check.workflowRuleName) {
      // IDS-Regel aus dem IDS-Editor (workflow_check_rules): am IFCCheck liegt nur der Namens-Snapshot.
      return this.check.workflowRuleName;
    } else if (this.check.lodType) {
      return (this.lodPipe.transform(this.check.lodType) as string | null) ?? 'Unbekannt';
    }
    return 'Unbekannt';
  }

  /**
   * Laedt den Bericht. Der LOIplus-Check schreibt seine IDS-Ergebnisse in den IFCCheck, deshalb wird
   * zuerst dort gesucht. Faellt die Suche leer aus, stammt der Bericht aus einem myCheck-Prueflauf --
   * dann liefert `as-ifc-check` das CheckResult in derselben Form.
   */
  ngOnInit(): void {
    this.activatedRoute.params.subscribe(params => {
      if (params.reportId !== undefined) {
        this.check = {id: params.reportId}
      }
      if (!this.check.id) {
        this.loading = false;
        return;
      }

      this.ifcCheckPrefetchService.getCheck(this.check.id).subscribe({
        next: (check: IFCCheck) => {
          if (check && check.results) {
            this.check = check;
            this.parseIdsResults();
            this.loading = false;
          } else {
            this.loadFromCheckResult();
          }
        },
        error: () => this.loadFromCheckResult()
      });
    });
  }

  /** Bericht eines myCheck-Prueflaufs, in die IFCCheck-Form gebracht. */
  private loadFromCheckResult(): void {
    const projectId = this.stateConfigService.getProjectId();
    const basePath = (this.checkResultService as any).basePath || '';
    this.http.get<any>(`${basePath}/workflows/check-results/${this.check.id!}/as-ifc-check`, {
      params: {projectId: String(projectId)}
    }).subscribe({
      next: (check) => {
        if (check) {
          this.check = check as IFCCheck;
          this.parseIdsResults();
        }
        this.loading = false;
      },
      error: (err) => {
        console.error('Prüfbericht konnte nicht geladen werden:', err);
        this.loading = false;
      }
    });
  }

  ngAfterViewInit(): void {
    this.setupResizeObserver();
    this.applicabilityContainers.changes.subscribe(() => this.observeAllContainers());
    this.valueContainers.changes.subscribe(() => this.observeAllContainers());
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.observedElements.clear();
  }

  createTaskForSpecification(specification: IdsSpecification): void {
    const fileName = this.check.projectFile?.name ?? ''
    const task = {
      markup: {
        topic: {
          title: `Überarbeiten notwendig für: ${fileName} bezüglich Spezifikation: ${specification.name}`,
        }
      },
    }
    this.taskDetailService.setOpenedTask(task as Task);
  }

  createTaskForRequirement(requirement: IdsRequirement): void {
    const fileName = this.check.projectFile?.name ?? ''
    const specName = this.selectedSpecification?.name ?? ''
    const task = {
      markup: {
        topic: {
          title: `Überarbeiten notwendig für: ${fileName} bezüglich Anforderung: ${requirement.name} (${specName})`,
        }
      },
    }
    this.taskDetailService.setOpenedTask(task as Task);
  }

  navigateToBaseCheckReport(): void {
    this.router.navigate(['base-check'], {relativeTo: this.activatedRoute.parent});
  }

  downloadReport(): void {
    const formattedDate = `${this.datePipe.transform(this.check.checkDate, 'dd.MM.y')} - ${this.datePipe.transform(this.check.checkDate, 'HH:mm')}`;
    const allElements = this.idsCheckResult.specifications.reduce((sum, s) => sum + s.totalElements, 0);

    const payload = {
      ids_result: JSON.parse(this.check.results ?? '{}'),
      template_path: 'https://de-thinkbic-public.s3.eu-central-1.amazonaws.com/bericht_ids_check_template.xlsx',
      target_texts: {
        'Prüfung durchgeführt am:': formattedDate,
        'Prüfregelname:': this.ruleName,
        'Geprüftes Modell:': this.check?.projectFile?.name?.split('.').slice(0, -1).join('.') ?? '',
        'Geprüfte Bauteile:': allElements,
        'Gesamtergebnis:': (this.idsCheckResult.score * 100).toFixed(0) + '%'
      }
    };

    this.http.post('https://services.thinkbic.de/bim/fill-ids-template', payload,
      {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        }
      }).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'bericht_ids_check.xlsx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      error: (err) => {
        console.error('Failed to download IDS report:', err);
      }
    });
  }

  /**
   * Gibt ein flaches Array von Badge-Texten für die Applicabilities einer Spezifikation zurück.
   * Jeder Wert wird als eigener Badge angezeigt mit dem Label des Facettentyps.
   */
  getApplicabilityBadges(spec: IdsSpecification): string[] {
    const badges: string[] = [];
    for (const app of spec.applicabilities) {
      if (app.values.length > 0) {
        for (const val of app.values) {
          badges.push(`${app.facetTypeLabel}: ${val}`);
        }
      } else if (app.value) {
        badges.push(`${app.facetTypeLabel}: ${app.value}`);
      } else {
        badges.push(app.facetTypeLabel);
      }
    }
    return badges;
  }

  private setupResizeObserver(): void {
    this.resizeObserver = new ResizeObserver(() => {
      this.zone.runOutsideAngular(() => {
        this.recalculateAllOverflows();
      });
    });
    this.observeAllContainers();
  }

  private observeAllContainers(): void {
    if (!this.resizeObserver) return;

    const currentElements = new Set<Element>();
    const allContainers = [
      ...(this.applicabilityContainers?.toArray() ?? []),
      ...(this.valueContainers?.toArray() ?? [])
    ];

    for (const ref of allContainers) {
      currentElements.add(ref.nativeElement);
      if (!this.observedElements.has(ref.nativeElement)) {
        this.resizeObserver.observe(ref.nativeElement);
        this.observedElements.add(ref.nativeElement);
      }
    }

    for (const el of this.observedElements) {
      if (!currentElements.has(el)) {
        this.resizeObserver.unobserve(el);
        this.observedElements.delete(el);
      }
    }

    this.recalculateAllOverflows();
  }

  private recalculateAllOverflows(): void {
    this.calculateOverflow(this.applicabilityContainers);
    this.calculateOverflow(this.valueContainers);
  }

  private calculateOverflow(containers: QueryList<ElementRef<HTMLDivElement>>): void {
    if (!containers) return;
    containers.forEach((containerRef) => {
      const container = containerRef.nativeElement;
      const children = Array.from(container.children) as HTMLElement[];
      const badges = children.filter(c => c.tagName === 'BIC-BADGE');
      const overflowCounter = children.find(c => c.classList.contains('overflow-counter'));

      if (badges.length === 0) {
        if (overflowCounter) overflowCounter.style.display = 'none';
        return;
      }

      // Reset: alle Badges sichtbar, volle Breite, Counter versteckt
      badges.forEach(b => {
        b.style.removeProperty('display');
        b.style.removeProperty('flex-shrink');
        b.style.removeProperty('min-width');
        b.style.removeProperty('overflow');
      });
      if (overflowCounter) overflowCounter.style.display = 'none';

      if (badges.length <= 1) return; // Einzelner Badge braucht keine Overflow-Logik

      const containerRight = container.getBoundingClientRect().right;

      // Finde den ersten Badge der überläuft (aber mindestens Index 1 - Badge[0] bleibt immer sichtbar)
      let firstOverflowIdx = badges.length;
      for (let i = 1; i < badges.length; i++) {
        if (badges[i].getBoundingClientRect().right > containerRight + 4) {
          firstOverflowIdx = i;
          break;
        }
      }

      let hiddenCount = badges.length - firstOverflowIdx;

      if (hiddenCount > 0) {
        // Verstecke überlaufende Badges
        for (let i = firstOverflowIdx; i < badges.length; i++) {
          badges[i].style.display = 'none';
        }

        if (overflowCounter) {
          overflowCounter.textContent = '+' + hiddenCount;
          overflowCounter.style.display = '';

          // Prüfe ob der Counter selbst überläuft → einen weiteren Badge verstecken (aber nie Badge[0])
          while (firstOverflowIdx > 1 && overflowCounter.getBoundingClientRect().right > containerRight + 4) {
            firstOverflowIdx--;
            hiddenCount = badges.length - firstOverflowIdx;
            badges[firstOverflowIdx].style.display = 'none';
            overflowCounter.textContent = '+' + hiddenCount;
          }
        }

        // Badge[0] darf schrumpfen wenn er der einzige sichtbare ist
        badges[0].style.flexShrink = '1';
        badges[0].style.minWidth = '0';
        badges[0].style.overflow = 'hidden';
      }
    });
  }

  private parseIdsResults(): void {
    if (!this.check || !this.check.results) {
      return;
    }

    try {
      const parsed = JSON.parse(this.check.results);
      if (parsed && typeof parsed === 'object') {
        this.idsCheckResult = {
          score: parsed.score ?? 0,
          specifications: (parsed.specifications ?? [])
            .filter((spec: any) => Number(spec.totalElements ?? 0) > 0 || this.isBimBaseSpecification(spec.name))
            .map((spec: any) => ({
            name: spec.name ?? '',
            status: spec.status ?? false,
            applicabilities: (spec.applicabilities ?? []).map((app: any) => ({
              facetType: app.facetType ?? '',
              facetTypeLabel: app.facetTypeLabel ?? '',
              value: app.value ?? null,
              values: this.flattenValues(app.values ?? [], app.value)
            })),
            totalElements: Number(spec.totalElements ?? 0),
            result: spec.result ?? 0,
            requirements: (spec.requirements ?? []).map((req: any) => ({
              facetType: req.facetType ?? '',
              facetTypeLabel: req.facetTypeLabel ?? '',
              name: this.cleanDisplayValue(req.name ?? ''),
              value: req.value ?? null,
              values: this.flattenValues(req.values ?? [], req.value),
              cardinality: req.cardinality ?? '',
              result: req.result ?? 0,
              status: req.status ?? false,
              guidListCorrect: req.guidListCorrect ?? [],
              guidListFail: req.guidListFail ?? []
            }))
          }))
        };
      }
    } catch (e) {
      console.error('Failed to parse IDS check results:', e);
    }
  }

  /**
   * Versucht einen String als JSON-Array, Python-Style Array/Dict zu parsen.
   * Gibt ein Array lesbarer Strings zurück.
   */
  private parseStringToArray(value: string): string[] | null {
    if (!value) return null;
    const trimmed = value.trim();

    // Normalisiere single quotes zu double quotes für JSON-Parsing
    const jsonNormalized = trimmed.replace(/'/g, '"');

    // Array: ["a", "b"] oder ['a', 'b']
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(jsonNormalized);
        if (Array.isArray(parsed)) {
          return parsed.map(v => String(v).trim()).filter(v => v.length > 0);
        }
      } catch {
        // Fallback: manuell splitten
        const inner = trimmed.slice(1, -1);
        const parts = inner.split(',').map(s => s.trim().replace(/^['"]|['"]$/g, '').trim()).filter(s => s.length > 0);
        if (parts.length > 1) return parts;
      }
    }

    // Object/Dict: {"key": "value"} oder {'key': 'value'}
    if (trimmed.startsWith('{')) {
      try {
        const parsed = JSON.parse(jsonNormalized);
        if (typeof parsed === 'object' && parsed !== null) {
          return this.formatConstraintObject(parsed);
        }
      } catch {
        // Fallback: manuell parsen
        const inner = trimmed.slice(1, -1);
        const parts = inner.split(',').map(s => {
          const [key, val] = s.split(':').map(p => p.trim().replace(/^['"]|['"]$/g, '').trim());
          return key && val ? { key, val } : null;
        }).filter(Boolean) as { key: string; val: string }[];
        if (parts.length > 0) {
          return this.formatConstraintObject(
            Object.fromEntries(parts.map(p => [p.key, p.val]))
          );
        }
      }
    }

    return null;
  }

  /** IDS-Constraint-Keys zu lesbaren Labels */
  private readonly constraintLabels: Record<string, string> = {
    'minInclusive': '≥',
    'maxInclusive': '≤',
    'minExclusive': '>',
    'maxExclusive': '<',
    'pattern': 'Muster',
    'enumeration': 'Enum',
    'length': 'Länge',
    'minLength': 'Min. Länge',
    'maxLength': 'Max. Länge',
  };

  /**
   * Formatiert ein Constraint-Objekt zu lesbaren Strings.
   * z.B. {minInclusive: '0', maxInclusive: '1.8'} → ['≥ 0', '≤ 1.8']
   * Wenn min+max zusammen vorkommen, wird ein Bereich daraus: ['0 – 1.8']
   */
  private formatConstraintObject(obj: Record<string, any>): string[] {
    const keys = Object.keys(obj);

    // Spezialfall: Bereich (min + max)
    const minIncl = obj['minInclusive'];
    const maxIncl = obj['maxInclusive'];
    const minExcl = obj['minExclusive'];
    const maxExcl = obj['maxExclusive'];

    const hasMin = minIncl != null || minExcl != null;
    const hasMax = maxIncl != null || maxExcl != null;

    if (hasMin && hasMax && keys.length === 2) {
      const minVal = minIncl ?? minExcl;
      const maxVal = maxIncl ?? maxExcl;
      const minBracket = minIncl != null ? '[' : '(';
      const maxBracket = maxIncl != null ? ']' : ')';
      return [`${minBracket}${minVal} – ${maxVal}${maxBracket}`];
    }

    // Sonst: jede Constraint einzeln
    return keys.map(key => {
      const label = this.constraintLabels[key] ?? key;
      return `${label} ${obj[key]}`;
    });
  }

  /**
   * Nimmt das values-Array und den einzelnen value, parst JSON-Strings
   * und gibt ein flaches Array aller Einzelwerte zurück.
   */
  private flattenValues(values: any[], singleValue: any): string[] {
    const result: string[] = [];

    const processValue = (val: any) => {
      if (val == null) return;
      const str = String(val);
      const parsed = this.parseStringToArray(str);
      if (parsed) {
        result.push(...parsed);
      } else if (str.trim().length > 0) {
        result.push(str);
      }
    };

    if (values && values.length > 0) {
      values.forEach(v => processValue(v));
    } else if (singleValue != null) {
      processValue(singleValue);
    }

    return result;
  }

  /**
   * Bereinigt einen Anzeige-String: parst Arrays zu komma-getrennter Liste.
   */
  private cleanDisplayValue(value: string): string {
    const parsed = this.parseStringToArray(value);
    if (parsed) {
      return parsed.join(', ');
    }
    return value;
  }

  private isBimBaseSpecification(name: unknown): boolean {
    if (typeof name !== 'string') return false;
    const normalizedName = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return normalizedName.includes('bim base check')
      || normalizedName.includes('bim basis check')
      || normalizedName.includes('base check')
      || normalizedName.includes('basis check');
  }

  /** Erstellt beim Öffnen im Viewer ein BCF-Topic pro fehlgeschlagener Anforderung. */
  openPressed() {

    const topicInputs: TopicCreationData[] = []

    this.idsCheckResult.specifications.forEach(spec => {
      const failedRequirements = spec.requirements.filter(req => !req.status && req.guidListFail.length > 0);

      failedRequirements.forEach(req => {
        const requirementName = req.name?.trim() || 'Anforderung';
        const sentence = req.guidListCorrect.length === 0
          ? 'kein Bauteil erfüllt'
          : `${req.guidListCorrect.length}/${req.guidListCorrect.length + req.guidListFail.length} Bauteile erfüllt`;

        topicInputs.push({
          title: `${spec.name}: ${requirementName}`,
          description: `${requirementName}: ${sentence}`,
          guids: [...req.guidListFail],
          labels: ['IDS-Check'],
          specification: spec.name,
          requirement: requirementName,
        });
      });
    });

    this.bimViewerService.open(this.check.projectFile!, topicInputs);
  }
}
